import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { useAppState } from "../appContext";
import { useNotification } from "../components/Notification";
import { query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { GeoFirestore } from "geofirestore";

// ---------------------------------------------------------------------------
// sessionStorage cache helpers (TTL = 10 minutes)
// Prevents re-reading thousands of docs on every page navigation
// ---------------------------------------------------------------------------
const SS_TTL_MS = 10 * 60 * 1000; // 10 minutes
const SS_VENUES_KEY = "wt_venues_all";
const SS_SUMMARY_KEY = "wt_venues_summary";

const ssGet = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > SS_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const ssSet = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // sessionStorage quota exceeded — fail silently
  }
};

const ssDel = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const venuesCache = {};

// Fields kept in venuesSummary (mirrors createVenuesSummary.cjs)
const SUMMARY_FIELDS = [
  "id", "venue_name", "venue_city", "venue_state", "venue_slug",
  "internallink", "slug", "lat", "lon", "address1", "VENUE_ZIP",
  "borough", "county", "title_lowercase", "searchTerms", "imageUrl",
  "tzone", "createdAt", "modifieddate",
];

function buildSummary(venueData) {
  const summary = {};
  for (const field of SUMMARY_FIELDS) {
    if (venueData[field] !== undefined) summary[field] = venueData[field];
  }
  return summary;
}

const generateVenuesCacheKey = (searchTerms, skip, type, take) => {
  const searchKey = searchTerms?.join("-") || "all";
  return `${searchKey}-${skip}-${type}-${take}`;
};

export const useFetchVenuesWithSearch = (
  take = 20,
  skip = 0,
  searchTerms = [],
  type = "accordion"
) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [lastDoc, setLastDoc] = useState(null); // Track the last document for pagination
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVenues = async () => {
      setLoading(true);

      try {
        const cacheKey = generateVenuesCacheKey(searchTerms, skip, type, take);
        // Check if results for this query are already cached
        if (venuesCache[cacheKey]) {
          const cachedResult = venuesCache[cacheKey];
          setVenues(cachedResult.venues);
          setTotalItems(cachedResult.totalItems);
          setLastDoc(cachedResult.lastDoc);
          setLoading(false);
          return;
        }

        let venuesQuery = collection(db, "venues");

        if (searchTerms.length > 0) {
          venuesQuery = query(
            venuesQuery,
              where("searchTerms", "array-contains-any", searchTerms)
            )
        }
        const countSnapshot = await getCountFromServer(venuesQuery);

        const totalCount = countSnapshot.data().count;

        venuesQuery = query(
          venuesQuery,
          orderBy("title_lowercase", "asc"),
          limit(take)
        );

        if (skip > 0 && lastDoc) {
          venuesQuery = query(venuesQuery, startAfter(lastDoc));
        }

        const querySnapshot = await getDocs(venuesQuery);
        const fetchedVenues = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,  // Put AFTER spread to ensure document ID is used
        }));
        const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

        // Get total count of items
        // const totalCountSnapshot = await getDocs(query(venuesQuery));
        // const totalCount = totalCountSnapshot.size;

        // Cache the result
        venuesCache[cacheKey] = {
          venues: fetchedVenues,
          totalItems: totalCount,
          lastDoc: lastVisible,
        };

        setVenues(fetchedVenues);
        setTotalItems(totalCount);
        setLastDoc(lastVisible);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching venues:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [take, skip, searchTerms, type]);

  return { venues, loading, totalItems, error };
};

let lastCachedVenues = null; // Store the last cached data globally

export const useFetchVenues = (refresh = null, enableApi = true) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVenues = async () => {
      // refresh > 0 means an explicit re-fetch was requested (e.g. after a save).
      // Bust the sessionStorage cache so we get fresh data, but only once.
      if (refresh > 0) {
        ssDel(SS_VENUES_KEY);
      }

      // If enableApi is false, return session/memory cache without hitting Firestore.
      if (!enableApi) {
        const ssData = ssGet(SS_VENUES_KEY);
        if (ssData) {
          setVenues(ssData);
        } else if (lastCachedVenues) {
          setVenues(lastCachedVenues);
        }
        setLoading(false);
        return;
      }

      // Check sessionStorage first — survives page navigation within the same browser tab.
      const ssData = ssGet(SS_VENUES_KEY);
      if (ssData) {
        setVenues(ssData);
        lastCachedVenues = ssData;
        setLoading(false);
        return;
      }

      // Nothing cached — fetch from Firestore.
      setLoading(true);
      try {
        let venuesQuery = collection(db, "venues");
        venuesQuery = query(venuesQuery, orderBy("title_lowercase", "asc"));

        const querySnapshot = await getDocs(venuesQuery);
        const fetchedVenues = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        // Persist to sessionStorage (TTL 10 min) and in-memory fallback.
        ssSet(SS_VENUES_KEY, fetchedVenues);
        lastCachedVenues = fetchedVenues;

        setVenues(fetchedVenues);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching venues:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchVenues();
  }, [refresh, enableApi]);

  return { venues, loading, error };
};

// ⚡ Lightweight hook for dropdowns - uses venuesSummary (NO embeddings)
// Uses sessionStorage (TTL 10 min) so navigation never re-reads thousands of docs.
// If venuesSummary is empty, falls back to the full venues collection ONCE per
// session (result is cached in sessionStorage so subsequent calls are free).
// To avoid the fallback permanently, populate venuesSummary by running:
//   node admin-api/scripts/createVenuesSummary.cjs
let lastCachedSummaries = null;

export const useFetchVenuesSummary = (refresh = null, enableApi = true) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummaries = async () => {
      // refresh > 0 means an explicit re-fetch was requested — bust the cache.
      if (refresh > 0) {
        ssDel(SS_SUMMARY_KEY);
      }

      // If enableApi is false, serve from sessionStorage / memory cache without
      // hitting Firestore (the data will load once enableApi becomes true).
      if (!enableApi) {
        const ssData = ssGet(SS_SUMMARY_KEY);
        if (ssData) {
          setVenues(ssData);
        } else if (lastCachedSummaries) {
          setVenues(lastCachedSummaries);
        }
        setLoading(false);
        return;
      }

      // Check sessionStorage first — survives page navigation in the same tab.
      const ssData = ssGet(SS_SUMMARY_KEY);
      if (ssData) {
        setVenues(ssData);
        lastCachedSummaries = ssData;
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let summariesQuery = collection(db, "venuesSummary");
        summariesQuery = query(summariesQuery, orderBy("title_lowercase", "asc"));

        const querySnapshot = await getDocs(summariesQuery);
        const fetchedSummaries = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        if (fetchedSummaries.length === 0) {
          // venuesSummary is empty — fall back to the full venues collection.
          // This is expensive (3K+ reads) but happens at most ONCE per browser
          // session because the result is immediately stored in sessionStorage.
          console.warn(
            "⚠️ venuesSummary collection is empty. Falling back to full venues collection (expensive). " +
            "Run: node admin-api/scripts/createVenuesSummary.cjs to fix this permanently."
          );
          let venuesQuery = collection(db, "venues");
          venuesQuery = query(venuesQuery, orderBy("title_lowercase", "asc"));
          const venuesSnapshot = await getDocs(venuesQuery);
          const fallbackVenues = venuesSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          // Cache so the fallback never fires again this session.
          ssSet(SS_SUMMARY_KEY, fallbackVenues);
          lastCachedSummaries = fallbackVenues;
          setVenues(fallbackVenues);
          setLoading(false);
          return;
        }

        ssSet(SS_SUMMARY_KEY, fetchedSummaries);
        lastCachedSummaries = fetchedSummaries;
        setVenues(fetchedSummaries);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching venue summaries:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchSummaries();
  }, [refresh, enableApi]);

  return { venues, loading, error };
};

export const useFetchVenueById = (venueId) => {
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        if (!venueId) {
          console.log("useFetchVenueById: No venueId provided");
          setLoading(false);
          return;
        }
        setLoading(true);
        
        // Ensure venueId is a string
        const venueIdString = String(venueId);
        
        // Use compat SDK method instead of modular SDK
        const venueDoc = await db.collection("venues").doc(venueIdString).get();
        
        if (venueDoc.exists) {
          const venueData = {
            id: venueDoc.id,
            ...venueDoc.data(),
          };
          setVenue(venueData);
        } else {
          setError("Venue not found");
        }
      } catch (err) {
        console.error(`useFetchVenueById: Error fetching venue:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [venueId]);

  return { venue, loading, error };
};

// Custom hook to add a new venue to Firestore
export const useAddVenue = () => {
  const { showSuccessMessage, showWarningMessage } = useNotification();

  const [loading, setLoading] = useState(false);

  const addVenue = async (venueData) => {
    try {
      setLoading(true);
      await setDoc(doc(db, "venues", `${venueData.id}`), venueData);

      // Get the event collection reference
      const venueCollection = new GeoFirestore(db).collection("venues");

      // Add the event data with geolocation using GeoFirestore's set method
      await venueCollection.doc(`${venueData.id}`).set({
        ...venueData,
      });

      // Keep venuesSummary in sync so dropdowns show the new venue immediately
      await setDoc(doc(db, "venuesSummary", `${venueData.id}`), buildSummary(venueData));
      ssDel(SS_SUMMARY_KEY);

      showSuccessMessage("Venue added successfully");
    } catch (error) {
      showWarningMessage("Error adding venue:", error);
    } finally {
      setLoading(false);
    }
  };

  return { addVenue, loading };
};

// Custom hook to update an existing venue in Firestore
export const useUpdateVenue = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccessMessage, showWarningMessage } = useNotification();

  const updateVenue = async (venueId, venueData) => {
    try {
      setLoading(true);
      const venueRef = doc(db, "venues", venueId + "");
      await updateDoc(venueRef, venueData);

      // Sync any summary fields that changed
      const summaryUpdate = buildSummary(venueData);
      if (Object.keys(summaryUpdate).length > 0) {
        await setDoc(doc(db, "venuesSummary", venueId + ""), summaryUpdate, { merge: true });
        ssDel(SS_SUMMARY_KEY);
      }

      showSuccessMessage("Venue Updated successfully");
    } catch (error) {
      console.log(error, venueId);
      showWarningMessage("Failed  updating Venue:", error);
    } finally {
      setLoading(false);
    }
  };

  return { updateVenue, loading };
};
// Custom hook to delete an venue from Firestore
export const useDeleteVenue = () => {
  const [loading, setLoading] = useState(false);
  const [state, dispatch] = useAppState();

  const { showSuccessMessage, showWarningMessage } = useNotification();

  const deleteVenue = async (venueId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, "venues", venueId));
      dispatch({
        type: "SET_PROP",
        payload: {
          key: "venues",
          value: state?.venues.filter((e) => e.id !== venueId),
        },
      });
      showSuccessMessage("Venue Deleted successfully");
    } catch (error) {
      showWarningMessage("Error Deleting Venue:", error);
    } finally {
      setLoading(false);
    }
  };

  return { deleteVenue, loading };
};
export const useFetchUtilityData = () => {
  const [utilityData, setUtilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUtilityData = async () => {
      try {
        const utilityRef = db.collection("utility").doc("1");
        const doc = await utilityRef.get();
        if (doc.exists) {
          setUtilityData(doc.data());
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUtilityData();
  }, []);

  return { utilityData, loading, error };
};
export const useFetchVenueByInternalLink = () => {
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchVenue = async (internallink) => {
    if (!internallink) {
      setError("Internal link is required");
      return;
    }

    try {
      setLoading(true);
      const venuesRef = collection(db, "venues");
      const q = query(
        venuesRef,
        where("internallink", "==", `/${internallink.toLowerCase()}/`)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const venueDoc = querySnapshot.docs[0]; // Assuming internallink is unique
        setVenue({
          id: venueDoc.id,
          ...venueDoc.data(),
        });
        return { id: venueDoc.id, ...venueDoc.data() };
      } else {
        setError("Venue not found");
      }
    } catch (err) {
      setError(`Error fetching venue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return { venue, loading, error, fetchVenue };
};
